import React, { useCallback } from "react";

import { faQuestionCircle } from "@fortawesome/pro-regular-svg-icons/faQuestionCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Space from "antd/es/space";
import { useIntl } from "react-intl";

import { Button, Modal } from "@mapmycustomers/ui";

import { AnalyticsService } from "@app/util/analytic/AnalyticsService";

import styles from "./InterceptNavigationMessageModal.module.scss";

interface Props {
  analyticIssuer?: AnalyticsService;
  leaveButtonText?: string;
  loading?: boolean;
  onLeave: () => void;
  onSave?: () => void;
  onStay: () => void;
  saveButtonText?: string;
  stayButtonText?: string;
  subTitle?: string;
  title?: string;
  visible: boolean;
}

const InterceptNavigationMessageModal = ({
  analyticIssuer,
  leaveButtonText,
  loading,
  onLeave,
  onSave,
  onStay,
  saveButtonText,
  stayButtonText,
  subTitle,
  title,
  visible,
}: Props) => {
  const intl = useIntl();

  const handleLeave = useCallback(() => {
    analyticIssuer?.clicked(["Discard changes"]);
    onLeave?.();
  }, [analyticIssuer, onLeave]);

  const handleStay = useCallback(() => {
    analyticIssuer?.clicked(["Keep Editing"]);
    onStay?.();
  }, [analyticIssuer, onStay]);

  const handleSave = useCallback(() => {
    analyticIssuer?.clicked(["Save and Exit"]);
    onSave?.();
  }, [analyticIssuer, onSave]);

  return (
    <Modal
      closable={false}
      footer={
        <div className={styles.footer}>
          <Button
            className={styles.saveButton}
            danger
            disabled={loading}
            key="leave"
            onClick={handleLeave}
          >
            {leaveButtonText ??
              intl.formatMessage({
                id: "component.interceptNavigationModal.leave",
                defaultMessage: "Discard changes",
              })}
          </Button>
          <Space>
            <Button disabled={loading} key="stay" onClick={handleStay}>
              {stayButtonText ??
                intl.formatMessage({
                  id: "component.interceptNavigationModal.stay",
                  defaultMessage: "Keep editing",
                })}
            </Button>
            {onSave && (
              <Button
                disabled={loading}
                key="save"
                loading={loading}
                onClick={handleSave}
                type="primary"
              >
                {saveButtonText ??
                  intl.formatMessage({
                    id: "component.interceptNavigationModal.save",
                    defaultMessage: "Save changes",
                  })}
              </Button>
            )}
          </Space>
        </div>
      }
      icon={<FontAwesomeIcon icon={faQuestionCircle} />}
      maskClosable={false}
      open={visible}
      size="small"
      title={
        title ??
        intl.formatMessage({
          id: "component.interceptNavigationModal.title",
          defaultMessage: "Save changes?",
        })
      }
      type="warning"
    >
      <span>
        {subTitle ??
          intl.formatMessage({
            id: "component.interceptNavigationModal.subTitle",
            defaultMessage:
              "If you leave before saving, your changes will be lost. Please save your updates before exiting.",
            description: "Intercept navigation modal subtitle",
          })}
      </span>
    </Modal>
  );
};

export default InterceptNavigationMessageModal;
